import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { getIDFromUrl } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { Workspace } from '@rossum/api-client/workspaces';
import {
  FilterDateRange,
  FilterMultiSelect,
  FilterNumberValues,
  FilterSelect,
} from '@rossum/components/filtering/components';
import { createSettings } from '@rossum/components/filtering/helpers';
import { FilterComponentProps } from '@rossum/components/filtering/types';
import {
  convertFromUTC,
  convertToUTC,
  formatDateHoursSecondsToQuery,
  formatDateTimeWithSecondsPickerValue,
} from '../../../lib/dateUtils';

const EXECUTION_RESULT_OPTIONS = [
  'success',
  'failure',
  'partial_success',
] as const;
const TRIGGER_EVENT_OPTIONS = [
  'validation',
  'annotation_imported',
  'annotation_import_succeeded',
  'annotation_import_failed',
  'annotation_confirmed',
  'annotation_exported',
  'annotation_export_succeeded',
  'annotation_export_failed',
] as const;
const TRIGGER_MATCH_OPTIONS = ['true', 'false'] as const;

export const rulesExecutionLogsQueryParser = z.object({
  createdAtAfter: z.coerce.string().optional(),
  createdAtBefore: z.coerce.string().optional(),
  search: z.coerce.string().optional(),
  page: z.coerce.number().optional(),
  pageSize: z.coerce.number().optional(),
  rule: z.coerce
    .string()
    .transform(val => val.split(',').map(Number))
    .pipe(z.number().array())
    .optional()
    .catch(undefined),
  annotation: z.coerce
    .string()
    .transform(val => val.split(',').map(Number))
    .pipe(z.number().array())
    .optional()
    .catch(undefined),
  queue: z.coerce
    .string()
    .transform(val => val.split(',').map(Number))
    .pipe(z.number().array())
    .optional()
    .catch(undefined),
  executionResult: z.coerce
    .string()
    .transform(val => val.split(','))
    .pipe(z.enum(EXECUTION_RESULT_OPTIONS).array())
    .optional()
    .catch(undefined),
  triggerEvent: z.coerce
    .string()
    .transform(val => val.split(','))
    .pipe(z.enum(TRIGGER_EVENT_OPTIONS).array())
    .optional()
    .catch(undefined),
  triggerMatch: z.enum(TRIGGER_MATCH_OPTIONS).optional().catch(undefined),
});

type RulesExecutionLogsQuery = Partial<
  z.infer<typeof rulesExecutionLogsQueryParser>
>;

type UseRulesExecutionLogsFilterSettingsProps = {
  queues: Queue[] | undefined;
  workspaces: Workspace[] | undefined;
  isQueuesLoading: boolean;
  setFilters: (state: RulesExecutionLogsQuery) => void;
};

export const useRulesExecutionLogsFilterSettings = ({
  queues,
  workspaces,
  isQueuesLoading,
  setFilters,
}: UseRulesExecutionLogsFilterSettingsProps) => {
  const intl = useIntl();

  const workspaceOptions = useMemo(() => {
    return workspaces
      ? workspaces
          .map(w => ({
            id: w.id.toString(),
            label: w.name,
            options: w.queues.map(q => getIDFromUrl(q).toString()),
          }))
          .filter(ws => ws.options.length > 0)
      : undefined;
  }, [workspaces]);

  return [
    createSettings({
      filterKey: 'executionResult',
      options: [...EXECUTION_RESULT_OPTIONS],
      optionsAreLoading: false,
      name: intl.formatMessage({
        id: 'features.rulesExecutionLogs.filters.executionResult',
      }),
      getChipLabel: value =>
        [...EXECUTION_RESULT_OPTIONS]
          .filter(o => value.includes(o))
          .map(v => {
            if (v === 'success')
              return intl.formatMessage({
                id: 'features.rulesExecutionLogs.status.success',
              });
            if (v === 'failure')
              return intl.formatMessage({
                id: 'features.rulesExecutionLogs.status.failure',
              });
            return intl.formatMessage({
              id: 'features.rulesExecutionLogs.status.partial_success',
            });
          })
          .join(', '),
      getOptionLabel: option => {
        if (option === 'success')
          return intl.formatMessage({
            id: 'features.rulesExecutionLogs.status.success',
          });
        if (option === 'failure')
          return intl.formatMessage({
            id: 'features.rulesExecutionLogs.status.failure',
          });
        return intl.formatMessage({
          id: 'features.rulesExecutionLogs.status.partial_success',
        });
      },
      valueGetter: (currentValue: RulesExecutionLogsQuery) =>
        currentValue?.executionResult,
      setFilter: value => setFilters({ executionResult: value, page: 1 }),
      Component: FilterMultiSelect,
    }),
    createSettings({
      filterKey: 'triggerEvent',
      options: [...TRIGGER_EVENT_OPTIONS],
      optionsAreLoading: false,
      name: intl.formatMessage({
        id: 'features.rulesExecutionLogs.filters.triggerEvent',
      }),
      getChipLabel: value =>
        [...TRIGGER_EVENT_OPTIONS].filter(o => value.includes(o)).join(', '),
      valueGetter: (currentValue: RulesExecutionLogsQuery) =>
        currentValue?.triggerEvent,
      setFilter: value => setFilters({ triggerEvent: value, page: 1 }),
      Component: FilterMultiSelect,
    }),
    createSettings({
      filterKey: 'triggerMatch',
      options: [...TRIGGER_MATCH_OPTIONS],
      optionsAreLoading: false,
      name: intl.formatMessage({
        id: 'features.rulesExecutionLogs.filters.triggerResult',
      }),
      getChipLabel: value =>
        intl.formatMessage({
          id:
            value === 'true'
              ? 'features.rulesExecutionLogs.triggerResult.true'
              : 'features.rulesExecutionLogs.triggerResult.false',
        }),
      getOptionLabel: option =>
        intl.formatMessage({
          id:
            option === 'true'
              ? 'features.rulesExecutionLogs.triggerResult.true'
              : 'features.rulesExecutionLogs.triggerResult.false',
        }),
      valueGetter: (currentValue: RulesExecutionLogsQuery) =>
        currentValue?.triggerMatch,
      setFilter: value => setFilters({ triggerMatch: value, page: 1 }),
      Component: FilterSelect,
    }),
    createSettings({
      filterKey: 'createdAt',
      options: [],
      name: intl.formatMessage({
        id: 'features.rulesExecutionLogs.filters.createdAt',
      }),
      getChipLabel: v =>
        v[0] && v[1]
          ? formatDateTimeWithSecondsPickerValue(v[0], v[1], intl.locale)
          : 'N/A',
      valueGetter: ({
        createdAtAfter,
        createdAtBefore,
      }: RulesExecutionLogsQuery) => {
        if (!createdAtAfter || !createdAtBefore) return undefined;

        const beginDate = createdAtAfter
          ? convertFromUTC(new Date(createdAtAfter))
          : null;
        const endDate = createdAtBefore
          ? convertFromUTC(new Date(createdAtBefore))
          : null;

        return [beginDate, endDate] as [Date | null, Date | null];
      },
      setFilter: value => {
        if (!value)
          return setFilters({
            createdAtAfter: undefined,
            createdAtBefore: undefined,
            page: 1,
          });

        const [from, to] = value;

        return setFilters({
          createdAtAfter: from
            ? formatDateHoursSecondsToQuery(convertToUTC(from))
            : undefined,
          createdAtBefore: to
            ? formatDateHoursSecondsToQuery(convertToUTC(to))
            : undefined,
          page: 1,
        });
      },
      Component: (
        props: FilterComponentProps<[Date | null, Date | null] | undefined>
      ) => <FilterDateRange {...props} isRange locale={intl.locale} />,
    }),
    createSettings({
      filterKey: 'annotation',
      options: [],
      name: intl.formatMessage({
        id: 'features.rulesExecutionLogs.filters.annotationIds',
      }),
      getChipLabel: (value: number[]) => value.join(', '),
      valueGetter: (currentValue: RulesExecutionLogsQuery) =>
        currentValue?.annotation,
      setFilter: (value: number[] | undefined) =>
        setFilters({ annotation: value, page: 1 }),
      Component: FilterNumberValues,
    }),
    createSettings({
      filterKey: 'queue',
      options: workspaceOptions ?? [],
      optionsAreLoading: isQueuesLoading,
      name: intl.formatMessage({
        id: 'features.rulesExecutionLogs.filters.queues',
      }),
      getOptionLabel: (option: string) =>
        queues?.find(q => q.id === Number(option))?.name ?? '',
      getChipLabel: (value: number[]) =>
        value
          .map(v => queues?.find(q => q.id === v)?.name ?? '')
          .filter(Boolean)
          .join(', '),
      valueGetter: (currentValue: RulesExecutionLogsQuery) =>
        currentValue?.queue,
      validateFilter: (value: number[]) =>
        !value.every(v => queues?.map(q => q.id).includes(v)),
      setFilter: (value: number[] | undefined) =>
        setFilters({ queue: value, page: 1 }),
      Component: FilterMultiSelect,
    }),
    createSettings({
      filterKey: 'rule',
      options: [],
      name: intl.formatMessage({
        id: 'features.rulesExecutionLogs.filters.ruleIds',
      }),
      getChipLabel: (value: number[]) => value.join(', '),
      valueGetter: (currentValue: RulesExecutionLogsQuery) =>
        currentValue?.rule,
      setFilter: (value: number[] | undefined) =>
        setFilters({ rule: value, page: 1 }),
      Component: FilterNumberValues,
    }),
  ];
};
